import apiClient from '../axios.js'

/**
 * 客戶資料管理 API
 */
export const clientService = {
    /**
     * 獲取客戶列表
     * @param {Object} params - 查詢參數
     * @param {number} params.page - 頁碼
     * @param {number} params.pageSize - 每頁數量
     * @param {string} params.search - 搜尋關鍵字
     * @param {string} params.platformId - 平台 ID（可選）
     * @param {string} params.status - 狀態（可選）
     * @returns {Promise<Object>} 客戶列表
     */
    getClients: async (params = {}) => {
        const response = await apiClient.get('/system/clients', { params })
        return response
    },

    /**
     * 獲取單一客戶詳情
     * @param {string} id - 客戶 ID
     * @returns {Promise<Object>} 客戶詳情
     */
    getClient: async (id) => {
        const response = await apiClient.get(`/system/clients/${id}`)
        return response
    },

    /**
     * 新增客戶
     * @param {Object} clientData - 客戶資料
     * @param {string} clientData.platformId - 平台 ID
     * @param {string} clientData.clientCode - 客戶代碼
     * @param {string} clientData.clientName - 客戶名稱
     * @param {string} clientData.industry - 行業（可選）
     * @param {string} clientData.status - 狀態（可選，預設 active）
     * @param {number} clientData.maxStores - 門市數上限（可選）
     * @param {number} clientData.maxMembers - 會員數上限（可選）
     * @param {string} clientData.billingMethod - 付款方式（可選，預設 manual_billing）
     * @param {string} clientData.billingStatus - 付款狀態（可選，預設 active）
     * @returns {Promise<Object>} 新增結果
     */
    createClient: async (clientData) => {
        const response = await apiClient.post('/system/clients', clientData)
        return response
    },

    /**
     * 更新客戶
     * @param {string} id - 客戶 ID
     * @param {Object} clientData - 客戶資料
     * @returns {Promise<Object>} 更新結果
     */
    updateClient: async (id, clientData) => {
        const response = await apiClient.put(`/system/clients/${id}`, clientData)
        return response
    },

    /**
     * 刪除客戶
     * @param {string} id - 客戶 ID
     * @returns {Promise<Object>} 刪除結果
     */
    deleteClient: async (id) => {
        const response = await apiClient.delete(`/system/clients/${id}`)
        return response
    },

    /**
     * 獲取客戶統計資訊
     * @returns {Promise<Object>} 客戶統計資訊
     */
    getClientStats: async () => {
        const response = await apiClient.get('/system/clients/stats')
        return response
    },

    /**
     * 生成客戶代碼（系統生成）
     * @param {string} platformId - 平台 ID
     * @returns {Promise<Object>} 生成的客戶代碼
     */
    generateClientCode: async (platformId) => {
        const response = await apiClient.post('/system/clients/generate-code', { platformId })
        return response
    },

    /**
     * 複製客戶（人工收款、維持原方案、勾選複製單元、建立管理員帳號）
     * @param {string} sourceClientId - 來源客戶 ID
     * @param {Object} body - 新客戶代碼／名稱、username／email（通常同為登入 Email）、密碼與勾選項目
     */
    duplicateClient: async (sourceClientId, body) => {
        const response = await apiClient.post(`/system/clients/${sourceClientId}/duplicate`, body)
        return response
    },

    /**
     * 為客戶建立 API 憑證
     * @param {string} clientId - 客戶 ID
     * @param {Object} data - 憑證資料
     * @param {string} data.name - 憑證名稱（備註）
     * @returns {Promise<Object>} 建立結果（含 apiKey 和 apiSecret）
     */
    createApiCredential: async (clientId, data = {}) => {
        const response = await apiClient.post(`/system/clients/${clientId}/api-credentials`, data)
        return response
    },

    /**
     * 查詢客戶的 API 憑證
     * @param {string} clientId - 客戶 ID
     * @returns {Promise<Object>} 憑證資訊（不含 apiSecret）
     */
    getApiCredential: async (clientId) => {
        const response = await apiClient.get(`/system/clients/${clientId}/api-credentials`)
        return response
    },

    /**
     * 輪換客戶的 API Secret
     * @param {string} clientId - 客戶 ID
     * @returns {Promise<Object>} 輪換結果（含新的 apiSecret）
     */
    rotateApiSecret: async (clientId) => {
        const response = await apiClient.post(`/system/clients/${clientId}/api-credentials/rotate-secret`)
        return response
    },

    /**
     * 撤銷客戶的 API 憑證
     * @param {string} clientId - 客戶 ID
     * @returns {Promise<Object>} 撤銷結果
     */
    revokeApiCredential: async (clientId) => {
        const response = await apiClient.delete(`/system/clients/${clientId}/api-credentials`)
        return response
    },

    /**
     * 查詢客戶 LINE 設定（系統/平台管理員專用）
     * @param {string} clientId - 客戶 ID
     * @returns {Promise<Object>} LINE 設定
     */
    getLineSettings: async (clientId) => {
        const response = await apiClient.get(`/system/clients/${clientId}/line-settings`)
        return response
    },

    /**
     * 更新客戶 LINE 設定（系統/平台管理員專用）
     * @param {string} clientId - 客戶 ID
     * @param {Object} settings - LINE 設定
     * @param {string} settings.crossOaBindingMode - 跨 OA 會員共用模式
     * @returns {Promise<Object>} 更新結果
     */
    updateLineSettings: async (clientId, settings) => {
        const response = await apiClient.patch(`/system/clients/${clientId}/line-settings`, settings)
        return response
    },

    getSmsPoints: async (clientId, params = {}) => {
        return apiClient.get(`/system/clients/${clientId}/sms-points`, { params })
    },

    addSmsPoints: async (clientId, payload) => {
        return apiClient.post(`/system/clients/${clientId}/sms-points`, payload)
    },
}
